import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { dayLabel, quantity, recurringFrequencyText } from '../labels';
import { Loader, SectionTitle, useLoad, useNotify, useRun } from '../common';

function recurringSubtitle(item: T.RecurringItem): string {
  const parts = [recurringFrequencyText(item.frequency)];
  const amount = quantity(item.quantity, item.unit);
  if (amount) parts.push(amount);
  if (item.storeName) parts.push(item.storeName);
  parts.push(item.isOnList ? 'já está na lista' : item.isDue ? 'a comprar' : `próxima: ${dayLabel(item.nextDueOn)}`);
  return parts.join(' · ');
}

/** Regular purchases: the due ones to add to the list (or skip), and all of them to edit. */
const RecurringItemsPage: React.FC = () => {
  const navigate = useNavigate();
  const run = useRun();
  const notify = useNotify();
  const { data: items, error, loading, reload } = useLoad(() => api.getRecurringItems());
  const [selected, setSelected] = useState<string[]>([]);
  const [applying, setApplying] = useState(false);

  const due = (items ?? []).filter(i => i.isDue && !i.isOnList);

  // Due and not on the list yet: ticked by default
  useEffect(() => {
    setSelected((items ?? []).filter(i => i.isDue && !i.isOnList).map(i => i.publicId));
  }, [items]);

  const apply = async () => {
    const add = due.filter(i => selected.includes(i.publicId)).map(i => i.publicId);
    const skip = due.filter(i => !selected.includes(i.publicId)).map(i => i.publicId);
    setApplying(true);
    const result = await run(() => api.applyRecurringItems({ add, skip }));
    setApplying(false);
    if (!result) return;
    await reload();
    notify(
      add.length === 0 ? 'Ficam para a próxima vez.' : add.length === 1 ? '1 artigo juntado a "Por comprar".' : `${add.length} artigos juntados a "Por comprar".`,
      { severity: 'success' },
    );
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Voltar" onClick={() => navigate('/casa/compras')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, flexGrow: 1 }}>Compras regulares</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/casa/compras/regulares/nova')}>Nova</Button>
      </Stack>

      <Loader loading={loading} error={error} onRetry={reload} hasData={items !== undefined}>
        {error && items && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {due.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Está na altura de comprar</Typography>
            <Typography variant="body2" color="text.secondary">
              Os marcados vão para "Por comprar"; os outros ficam para a próxima vez.
            </Typography>
            <List dense>
              {due.map(item => (
                <ListItem key={item.publicId} disablePadding>
                  <ListItemButton
                    onClick={() => setSelected(s => (s.includes(item.publicId) ? s.filter(x => x !== item.publicId) : s.concat(item.publicId)))}
                    sx={{ px: 0 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox edge="start" checked={selected.includes(item.publicId)} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText primary={item.name} secondary={recurringSubtitle(item)} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Button variant="contained" onClick={apply} disabled={applying}>Juntar à lista</Button>
          </Paper>
        )}

        <Box>
          <SectionTitle>Todas</SectionTitle>
          {items && items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Ainda não há compras regulares. Usa «Nova» para juntar coisas como papel higiénico ou detergente, que se compram de tempos a tempos.
            </Typography>
          )}
          {items && items.length > 0 && (
            <Paper variant="outlined">
              <List disablePadding>
                {items.map(item => (
                  <ListItemButton key={item.publicId} divider onClick={() => navigate(`/casa/compras/regulares/${item.publicId}`)}>
                    <ListItemText primary={item.name} secondary={recurringSubtitle(item)} />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}
        </Box>
      </Loader>
    </>
  );
};

export default RecurringItemsPage;
